import { useLocation } from "wouter";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { Logo } from "@/components/logo";
import { Home, LineChart, PlusCircle, Settings, FileText, Zap, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();

  async function handleLogout() {
    await logout();
    setLocation("/login");
  }

  if (!user) {
    return <>{children}</>;
  }

  const initials = user.email
    .split("@")[0]
    .split(".")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const navItems = [
    {
      href: "/",
      label: "Home",
      icon: Home,
    },
    {
      href: "/chat",
      label: "Research Chat",
      icon: PlusCircle,
    },
    {
      href: "/analytics",
      label: "Analytics",
      icon: LineChart,
    },
    {
      href: "/signals",
      label: "Signals",
      icon: Zap,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between px-8">
          <button
            onClick={() => setLocation("/")}
            className="hover-elevate rounded-lg p-2 -ml-2"
            data-testid="button-logo"
            title="Go to Home"
            aria-label="Go to Home"
          >
            <Logo />
          </button>

          <div className="flex items-center gap-4">
            {user?.role === 'beta' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="hidden md:flex items-center rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary cursor-default">
                    <span className="mr-1 text-primary">●</span>
                    <span>AcronIQ Veritus</span>
                    <span className="mx-1">·</span>
                    <span>Beta</span>
                    <span className="ml-1 text-[0.7rem] text-primary/80">Evaluation access</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="center">
                  <p className="max-w-xs text-xs text-muted-foreground">
                    You are using the AcronIQ Veritus Beta for evaluation and feedback. Features, models, and pricing may change.
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            {navItems.map(({ href, label, icon: Icon }) => (
              <Button
                key={href}
                variant={location === href ? "default" : "ghost"}
                onClick={() => setLocation(href)}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {label}
              </Button>
            ))}
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-10 w-10 rounded-full"
                  data-testid="button-user-menu"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">Account</p>
                    <p className="text-xs text-muted-foreground" data-testid="text-user-email">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="button-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12">
        {children}
      </main>
    </div>
  );
}